// frontend/src/hooks/useFileUpload.ts

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios, { AxiosError } from 'axios';
import {
  FileJobAcceptedResponse,
  FileJobStatusResponse,
  SnackbarState,
  UploadProgressState,
} from '../types';
import { getUploadErrorMessage } from '../utils/uploadErrorMessage';

const API_URL = process.env.REACT_APP_API_URL;
const SERVER_PROGRESS_START = 20;
const COMPLETED_PROGRESS_DISPLAY_MS = 600;

const sleep = (milliseconds: number) => new Promise((resolve) => {
  window.setTimeout(resolve, milliseconds);
});

class UploadJobError extends Error {
  constructor(public readonly errorCode: string) {
    super(errorCode);
  }
}

interface UploadMutationInput {
  formData: FormData;
  startPercentage: number;
}

const getUploadTotalSize = (formData: FormData): number => {
  const file = formData.get('file');
  return file instanceof Blob ? file.size : 0;
};

const clamp = (value: number, minimum: number, maximum: number): number =>
  Math.min(Math.max(value, minimum), maximum);

const createIdempotencyKey = (): string =>
  globalThis.crypto?.randomUUID?.() ??
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;

export const useFileUpload = () => {
  const [snackbar, setSnackbar] = useState<SnackbarState>({ open: false, message: '', severity: 'info' });
  const [progress, setProgress] = useState<UploadProgressState>({
    percentage: 0,
    processedBytes: 0,
    totalBytes: 0,
  });
  const queryClient = useQueryClient();

  const uploadMutation = useMutation({
    mutationFn: async ({ formData, startPercentage }: UploadMutationInput) => {
      const uploadTotalSize = getUploadTotalSize(formData);
      const normalizedStart = clamp(startPercentage, 0, 10);
      setProgress({
        percentage: normalizedStart,
        processedBytes: 0,
        totalBytes: uploadTotalSize,
      });

      const response = await axios.post<FileJobAcceptedResponse>(`${API_URL}/api/files/upload/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Idempotency-Key': createIdempotencyKey(),
        },
        onUploadProgress: (event) => {
          const total = event.total && event.total > 0 ? event.total : uploadTotalSize;
          const loaded = total > 0 ? clamp(event.loaded, 0, total) : Math.max(event.loaded, 0);
          const ratio = total > 0 ? loaded / total : 0;
          const percentage = normalizedStart + ratio * (SERVER_PROGRESS_START - normalizedStart);
          setProgress((previous) => ({
            percentage: Math.max(previous.percentage, percentage),
            processedBytes: loaded,
            totalBytes: total,
          }));
        },
      });

      setProgress((previous) => ({
        percentage: Math.max(previous.percentage, SERVER_PROGRESS_START),
        processedBytes: 0,
        totalBytes: 0,
      }));

      for (;;) {
        const jobResponse = await axios.get<FileJobStatusResponse>(
          `${API_URL}/api/files/jobs/${response.data.jobId}`,
        );
        const job = jobResponse.data;
        const serverProgress = clamp(job.progress, 0, 100);
        const overallProgress = job.status === 'completed'
          ? 100
          : Math.min(99, SERVER_PROGRESS_START + serverProgress * 0.8);
        setProgress((previous) => {
          const totalBytes = job.totalBytes ?? previous.totalBytes;
          const reportedBytes = job.processedBytes ?? previous.processedBytes;
          const processedBytes = previous.totalBytes === totalBytes
            ? Math.max(previous.processedBytes, reportedBytes)
            : reportedBytes;
          return {
            percentage: Math.max(previous.percentage, overallProgress),
            processedBytes,
            totalBytes,
          };
        });
        if (job.status === 'completed') {
          return job;
        }
        if (job.status === 'failed' || job.status === 'cancelled') {
          throw new UploadJobError(job.errorCode ?? 'upload_processing_failed');
        }
        await sleep(500);
      }
    },

    onSuccess: async () => {
      setProgress((previous) => ({ ...previous, percentage: 100 }));
      setSnackbar({
        open: true,
        message: 'ファイルのアップロードとサーバー処理が完了しました。',
        severity: 'success',
      });
      queryClient.invalidateQueries({ queryKey: ['files'] });
      queryClient.invalidateQueries({ queryKey: ['uploadLimits'] });
      await sleep(COMPLETED_PROGRESS_DISPLAY_MS);
    },

    onError: (error: AxiosError<{ detail?: string }> | UploadJobError) => {
      const message = error instanceof UploadJobError
        ? getUploadErrorMessage({
            response: { status: 400, data: { detail: error.errorCode } },
          } as AxiosError<{ detail?: string }>)
        : getUploadErrorMessage(error);
      setSnackbar({ open: true, message, severity: 'error' });
    },
    onSettled: () => {
      setProgress({ percentage: 0, processedBytes: 0, totalBytes: 0 });
    },
  });

  const handleCloseSnackbar = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar((previous) => ({ ...previous, open: false }));
  };

  return {
    uploadFile: (formData: FormData, startPercentage = 0) =>
      uploadMutation.mutate({ formData, startPercentage }),
    isUploading: uploadMutation.isPending,
    snackbar,
    progress,
    handleCloseSnackbar,
    setSnackbar,
  };
};
